"use client";
import KibbeConceptLinks from "@/components/kibbe/KibbeConceptLinks";

import { useState } from "react";
import {
  Container,
  Title,
  Text,
  Stack,
  Box,
  SimpleGrid,
  Group,
  Button,
  Image,
  Accordion,
  Paper,
  Anchor,
  ScrollArea,
} from "@mantine/core";
import { IconChevronRight } from "@tabler/icons-react";
import { Link } from "@/i18n/navigation";
import KibbeOffer from "@/components/kibbe/KibbeOffer";
import { trackCtaClick, trackGizmo } from "@/lib/analytics";
import { KibbeType, kibbeTypes, kibbeComparisons } from "../kibbeData";
import { kibbeExtended } from "../kibbeExtendedData";
import { kibbeCelebrities } from "../kibbeCelebrityData";
import EmailResultsCapture from "@/components/EmailResultsCapture";
import classes from "./type.module.css";

// Helper to check if a comparison exists
function comparisonExists(type1: string, type2: string): boolean {
  return kibbeComparisons.some(
    ([t1, t2]) => (t1 === type1 && t2 === type2) || (t1 === type2 && t2 === type1)
  );
}

interface KibbeTypeContentProps {
  typeData: KibbeType;
  locale?: string;
}

// Types with dedicated lookbook photography (image set 1)
const LOOKBOOK_TYPES = new Set([
  "dramatic",
  "soft-dramatic",
  "flamboyant-natural",
  "natural",
  "soft-natural",
  "dramatic-classic",
  "classic",
  "soft-classic",
  "flamboyant-gamine",
  "gamine",
  "soft-gamine",
  "theatrical-romantic",
  "romantic",
]);
// Types with a per-style hair gallery (4 images matching bestStyles order)
const HAIR_GALLERY_TYPES = new Set([
  "dramatic",
  "soft-dramatic",
  "flamboyant-natural",
  "natural",
  "soft-natural",
  "dramatic-classic",
  "classic",
  "soft-classic",
  "flamboyant-gamine",
  "gamine",
  "soft-gamine",
  "theatrical-romantic",
  "romantic",
]);
// Types with a dedicated annotated face image
const FACE_TYPES = new Set([
  "dramatic",
  "soft-dramatic",
  "flamboyant-natural",
  "natural",
  "soft-natural",
  "dramatic-classic",
  "classic",
  "soft-classic",
  "flamboyant-gamine",
  "gamine",
  "soft-gamine",
  "theatrical-romantic",
  "romantic",
]);
// Types with annotated characteristic diagrams (public/kibbe/characteristics/{id}/{bone|flesh|facial}.webp)
const CHARACTERISTIC_TYPES = new Set([
  "dramatic",
  "soft-dramatic",
  "flamboyant-natural",
  "natural",
  "soft-natural",
  "dramatic-classic",
  "classic",
  "soft-classic",
  "flamboyant-gamine",
  "gamine",
  "soft-gamine",
  "theatrical-romantic",
  "romantic",
]);

// Annotation callouts for the face section, keyed by type id.
// Positions are percentages within the square image box.
const faceAnnotations: Record<string, { label: string; dotX: number; dotY: number }[]> = {
  dramatic: [
    { label: "Narrow, elongated eyes", dotX: 40, dotY: 40 },
    { label: "Prominent cheekbones", dotX: 66, dotY: 51 },
    { label: "Sharp jawline", dotX: 50, dotY: 74 },
  ],
  "soft-dramatic": [
    { label: "Large, lush eyes", dotX: 40, dotY: 40 },
    { label: "Soft cheeks over sharp bones", dotX: 66, dotY: 51 },
    { label: "Strong jaw, softly finished", dotX: 50, dotY: 74 },
  ],
  "flamboyant-natural": [
    { label: "Wide-set eyes", dotX: 40, dotY: 40 },
    { label: "Blunt cheekbones", dotX: 66, dotY: 51 },
    { label: "Strong, broad jawline", dotX: 50, dotY: 74 },
  ],
  natural: [
    { label: "Open, friendly eyes", dotX: 40, dotY: 40 },
    { label: "Blunt cheekbones", dotX: 66, dotY: 51 },
    { label: "Moderate-to-wide jaw", dotX: 50, dotY: 74 },
  ],
  "soft-natural": [
    { label: "Wide, soft eyes", dotX: 40, dotY: 40 },
    { label: "Full cheeks", dotX: 66, dotY: 51 },
    { label: "Rounded nose tip", dotX: 50, dotY: 55 },
  ],
  "dramatic-classic": [
    { label: "Even, symmetrical eyes", dotX: 40, dotY: 40 },
    { label: "Slightly sharpened bones", dotX: 66, dotY: 51 },
    { label: "Defined, never extreme jaw", dotX: 50, dotY: 74 },
  ],
  classic: [
    { label: "Moderate, balanced eyes", dotX: 40, dotY: 40 },
    { label: "Even, moderate cheekbones", dotX: 66, dotY: 51 },
    { label: "Neither sharp nor soft jaw", dotX: 50, dotY: 74 },
  ],
  "soft-classic": [
    { label: "Large, gentle eyes", dotX: 40, dotY: 40 },
    { label: "Softly rounded cheeks", dotX: 66, dotY: 51 },
    { label: "Full, rounded lips", dotX: 50, dotY: 62 },
  ],
  "flamboyant-gamine": [
    { label: "Bright, alert eyes", dotX: 40, dotY: 40 },
    { label: "Prominent cheekbones", dotX: 66, dotY: 51 },
    { label: "Small, sharp nose", dotX: 50, dotY: 55 },
  ],
  gamine: [
    { label: "Large, yin eyes", dotX: 40, dotY: 40 },
    { label: "Contrasting mixed features", dotX: 66, dotY: 51 },
    { label: "Sharp nose or jaw", dotX: 50, dotY: 74 },
  ],
  "soft-gamine": [
    { label: "Large, round eyes", dotX: 40, dotY: 40 },
    { label: "Full cheeks", dotX: 66, dotY: 51 },
    { label: "Small, softly rounded nose", dotX: 50, dotY: 55 },
  ],
  "theatrical-romantic": [
    { label: "Large, liquid eyes", dotX: 40, dotY: 40 },
    { label: "Soft cheeks, defined nose", dotX: 66, dotY: 51 },
    { label: "Delicately tapered chin", dotX: 50, dotY: 74 },
  ],
  romantic: [
    { label: "Large, soft eyes", dotX: 40, dotY: 40 },
    { label: "Full cheeks", dotX: 66, dotY: 51 },
    { label: "Gently rounded jaw and chin", dotX: 50, dotY: 74 },
  ],
};

// Section counter helper component
function SectionHeader({
  n,
  label,
  title,
}: {
  n: number;
  label: string;
  title: string;
}) {
  return (
    <Box mb={24}>
      <p className={classes.kicker}>
        N&ordm; {String(n).padStart(2, "0")} &mdash; {label}
      </p>
      <h2 className={classes.sectionTitle}>{title}</h2>
      <hr className={classes.hairline} />
    </Box>
  );
}

// Outfit ideas for each type with descriptions
const outfitIdeas: Record<string, { name: string; description: string; pieces: string[] }[]> = {
  dramatic: [
    { name: "Power Meeting", description: "Command any boardroom with sharp, architectural pieces", pieces: ["Structured black blazer", "High-waisted wide-leg trousers", "Pointed-toe pumps", "Minimalist gold jewelry"] },
    { name: "Evening Elegance", description: "Sleek and striking for formal events", pieces: ["Column gown with clean lines", "Statement earrings", "Strappy heels", "Clutch with geometric design"] },
    { name: "Weekend Sharp", description: "Casual doesn't mean boring for Dramatics", pieces: ["Long leather coat", "Straight-leg dark jeans", "Fitted black turtleneck", "Ankle boots"] },
    { name: "Creative Professional", description: "Stand out while staying professional", pieces: ["Asymmetric blazer", "Monochrome separates", "Architectural jewelry", "Pointed flats"] },
  ],
  "soft-dramatic": [
    { name: "Glamorous Day", description: "Daytime drama with curves honored", pieces: ["Wrap blouse in silk", "High-waisted wide-leg pants", "Platform heels", "Bold statement necklace"] },
    { name: "Red Carpet Ready", description: "Full SD glamour for special occasions", pieces: ["Draped gown with deep V", "Chandelier earrings", "Strappy heels", "Embellished clutch"] },
    { name: "Sensual Casual", description: "Relaxed but never frumpy", pieces: ["Cashmere wrap sweater", "Flowing palazzo pants", "Wedge sandals", "Layered gold chains"] },
    { name: "Date Night", description: "Romantic with a dramatic edge", pieces: ["Fitted wrap dress", "Statement earrings", "Strappy heels", "Bold lip color"] },
  ],
  "flamboyant-natural": [
    { name: "Effortless Power", description: "Commanding presence with relaxed ease", pieces: ["Oversized structured blazer", "Wide-leg trousers", "Chunky loafers", "Bold geometric jewelry"] },
    { name: "Weekend Luxe", description: "Off-duty model vibes", pieces: ["Long cardigan coat", "Straight-leg jeans", "White sneakers", "Oversized tote bag"] },
    { name: "Evening Impact", description: "Make an entrance without trying too hard", pieces: ["Flowing maxi dress", "Statement cuff bracelet", "Strappy flat sandals", "Natural-toned clutch"] },
    { name: "Business Casual", description: "Professional with that FN edge", pieces: ["Relaxed blazer in camel", "Wide-leg pants", "Pointed mules", "Leather belt"] },
  ],
  natural: [
    { name: "Classic Casual", description: "The effortless natural look", pieces: ["Cotton button-down shirt", "Well-fitted jeans", "Leather loafers", "Simple watch"] },
    { name: "Smart Weekend", description: "Polished without being stiff", pieces: ["Soft knit sweater", "Chinos", "Canvas sneakers", "Crossbody bag"] },
    { name: "Easy Evening", description: "Relaxed elegance for dinner out", pieces: ["Silk blouse in neutral", "Tailored trousers", "Block heel sandals", "Small gold hoops"] },
    { name: "Active Chic", description: "From errands to lunch", pieces: ["Fitted tee", "Relaxed joggers", "Clean white sneakers", "Baseball cap"] },
  ],
  "soft-natural": [
    { name: "Effortless Chic", description: "The SN signature - looks thrown together but perfect", pieces: ["Oversized linen blazer", "Relaxed straight jeans", "Leather sandals", "Delicate layered necklaces"] },
    { name: "Beach to Brunch", description: "Flowy and feminine with that SN ease", pieces: ["Flowy midi dress", "Woven belt", "Wedge espadrilles", "Straw tote"] },
    { name: "Smart Casual", description: "Professional but approachable", pieces: ["Soft blazer in neutral", "Wide-leg trousers", "Pointed-toe flats", "Simple gold hoops"] },
    { name: "Weekend Vibes", description: "Comfortable but put-together", pieces: ["Oversized cardigan", "High-waisted jeans", "White sneakers", "Crossbody bag"] },
  ],
  "dramatic-classic": [
    { name: "Executive Presence", description: "Polished power with a sharp edge", pieces: ["Tailored blazer with sharp shoulders", "Pencil skirt", "Pointed-toe pumps", "Structured handbag"] },
    { name: "Sophisticated Evening", description: "Refined glamour", pieces: ["Sheath dress in solid color", "Statement earrings", "Strappy heels", "Envelope clutch"] },
    { name: "Elevated Casual", description: "Put-together without overdoing it", pieces: ["Structured cardigan", "Tailored trousers", "Leather flats", "Minimalist jewelry"] },
    { name: "Modern Classic", description: "Timeless with an edge", pieces: ["Fitted turtleneck", "High-waisted wide-leg pants", "Ankle boots", "Geometric pendant"] },
  ],
  classic: [
    { name: "Timeless Elegance", description: "The essence of classic style", pieces: ["Tailored blazer in navy", "White silk blouse", "Straight-leg trousers", "Pearl studs"] },
    { name: "Refined Weekend", description: "Casual doesn't mean sloppy", pieces: ["Cashmere crewneck", "Well-fitted jeans", "Leather loafers", "Simple gold watch"] },
    { name: "Evening Grace", description: "Understated sophistication", pieces: ["Little black dress", "Diamond studs", "Classic pumps", "Structured clutch"] },
    { name: "Office Ready", description: "Professional and polished", pieces: ["Fitted sheath dress", "Cardigan", "Nude pumps", "Leather tote"] },
  ],
  "soft-classic": [
    { name: "Feminine Polish", description: "Classic with a soft touch", pieces: ["Soft blazer with rounded shoulders", "Flowy blouse", "A-line skirt", "Delicate jewelry"] },
    { name: "Romantic Office", description: "Professional yet gentle", pieces: ["Wrap top in soft color", "Tailored trousers", "Kitten heels", "Pearl bracelet"] },
    { name: "Garden Brunch", description: "Soft and pretty", pieces: ["Floral midi dress", "Lightweight cardigan", "Strappy sandals", "Small crossbody"] },
    { name: "Date Night", description: "Romantic and refined", pieces: ["Draped dress in blush", "Drop earrings", "Pointed flats", "Satin clutch"] },
  ],
  "flamboyant-gamine": [
    { name: "Edgy Chic", description: "Sharp and playful mixed together", pieces: ["Cropped leather jacket", "High-waisted straight jeans", "Ankle boots", "Bold earrings"] },
    { name: "Statement Casual", description: "Never boring, always interesting", pieces: ["Graphic tee", "Tailored shorts", "Platform sneakers", "Geometric jewelry"] },
    { name: "Night Out", description: "Drama in a petite package", pieces: ["Structured mini dress", "Statement necklace", "Pointed-toe heels", "Clutch with edge"] },
    { name: "Creative Professional", description: "Artistic and put-together", pieces: ["Cropped blazer", "High-waisted pants", "Pointed flats", "Interesting earrings"] },
  ],
  gamine: [
    { name: "Classic Gamine", description: "The quintessential boyish-chic look", pieces: ["Breton stripe top", "Slim cropped pants", "Ballet flats", "Small crossbody bag"] },
    { name: "Mod Moment", description: "60s-inspired freshness", pieces: ["Shift dress", "Mary Jane shoes", "Statement earrings", "Structured mini bag"] },
    { name: "Casual Cool", description: "Effortlessly youthful", pieces: ["Fitted tee", "High-waisted jeans", "Canvas sneakers", "Bandana accessory"] },
    { name: "Evening Edge", description: "Playful sophistication", pieces: ["Tailored jumpsuit", "Statement belt", "Pointed-toe flats", "Geometric jewelry"] },
  ],
  "soft-gamine": [
    { name: "Playful Chic", description: "Cute and put-together", pieces: ["Fitted cardigan", "Pleated mini skirt", "Mary Jane heels", "Dainty jewelry"] },
    { name: "Date Ready", description: "Sweet with a hint of spice", pieces: ["Fit-and-flare dress", "Kitten heels", "Pearl earrings", "Small crossbody"] },
    { name: "Casual Cute", description: "Weekend wear with personality", pieces: ["Striped fitted tee", "High-waisted shorts", "Canvas sneakers", "Fun crossbody bag"] },
    { name: "Work Appropriate", description: "Professional but youthful", pieces: ["Cropped blazer", "Fitted trousers", "Block heels", "Structured small bag"] },
  ],
  "theatrical-romantic": [
    { name: "Glamorous Drama", description: "Curves meet sharp features", pieces: ["Fitted dress with draping", "Statement earrings", "Strappy heels", "Embellished clutch"] },
    { name: "Sensual Evening", description: "Old Hollywood glamour", pieces: ["Form-fitting gown", "Chandelier earrings", "Pointed-toe pumps", "Vintage-inspired clutch"] },
    { name: "Polished Day", description: "Dramatic femininity for daytime", pieces: ["Wrap blouse with detail", "Pencil skirt", "Kitten heels", "Bold necklace"] },
    { name: "Date Night Siren", description: "Romantic with an edge", pieces: ["Bodycon dress", "Statement jewelry", "Strappy sandals", "Small clutch"] },
  ],
  romantic: [
    { name: "Feminine Dream", description: "Pure romantic softness", pieces: ["Wrap dress with soft print", "Kitten heels", "Delicate jewelry", "Small structured bag"] },
    { name: "Evening Romance", description: "Soft glamour for special nights", pieces: ["Draped chiffon gown", "Pearl jewelry", "Strappy sandals", "Satin clutch"] },
    { name: "Soft Professional", description: "Romantic vibes at work", pieces: ["Soft blouse with bow detail", "Pencil skirt in soft fabric", "Rounded-toe pumps", "Pearl studs"] },
    { name: "Garden Party", description: "Feminine and fresh", pieces: ["Floral midi dress", "Delicate cardigan", "Strappy sandals", "Woven basket bag"] },
  ],
};

// Get outfit ideas for a type (with fallback)
function getOutfitIdeas(typeId: string) {
  return outfitIdeas[typeId] || [
    { name: "Classic Look 1", description: "A timeless combination for your type", pieces: ["Tailored top", "Well-fitted bottoms", "Appropriate shoes", "Complementary accessories"] },
    { name: "Evening Option", description: "Elevated style for special occasions", pieces: ["Elegant top or dress", "Refined bottoms", "Dressy shoes", "Statement accessories"] },
    { name: "Casual Comfort", description: "Relaxed but polished", pieces: ["Comfortable top", "Easy bottoms", "Casual shoes", "Simple accessories"] },
    { name: "Work Ready", description: "Professional and appropriate", pieces: ["Office-appropriate top", "Tailored bottoms", "Professional shoes", "Subtle accessories"] },
  ];
}

// cm -> ft'in" label, e.g. 165 -> 5'5"
function cmToFtIn(cm: number): string {
  const totalInches = cm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);
  return `${feet}'${inches}"`;
}

const HEIGHT_BAR_MIN = 145;
const HEIGHT_BAR_MAX = 190;
const HEIGHT_TICKS_CM = [150, 160, 170, 180];

function heightPercent(cm: number) {
  return ((cm - HEIGHT_BAR_MIN) / (HEIGHT_BAR_MAX - HEIGHT_BAR_MIN)) * 100;
}

export default function KibbeTypeContent({ typeData, locale }: KibbeTypeContentProps) {
  const [currentOutfitIndex, setCurrentOutfitIndex] = useState(0);
  const outfits = getOutfitIdeas(typeData.id);
  const extended = kibbeExtended[typeData.id];

  const relatedTypes = typeData.commonMistypes
    .map((name) => {
      const entry = Object.entries(kibbeTypes).find(([_, t]) => t.name === name);
      return entry ? entry[1] : null;
    })
    .filter(Boolean);

  // Yin/Yang balance for visualization
  const yinYangBalance = {
    dramatic: { yin: 10, yang: 90 },
    "soft-dramatic": { yin: 30, yang: 70 },
    "flamboyant-natural": { yin: 20, yang: 80 },
    natural: { yin: 35, yang: 65 },
    "soft-natural": { yin: 45, yang: 55 },
    "dramatic-classic": { yin: 40, yang: 60 },
    classic: { yin: 50, yang: 50 },
    "soft-classic": { yin: 60, yang: 40 },
    "flamboyant-gamine": { yin: 35, yang: 65 },
    gamine: { yin: 50, yang: 50 },
    "soft-gamine": { yin: 65, yang: 35 },
    "theatrical-romantic": { yin: 75, yang: 25 },
    romantic: { yin: 90, yang: 10 },
  }[typeData.id] || { yin: 50, yang: 50 };

  // Type-specific accent colors — used sparingly (hairlines, kickers, height band)
  const typeAccentColors: Record<string, { primary: string; gradient: string; light: string }> = {
    dramatic: { primary: "gray.9", gradient: "linear-gradient(135deg, #1a1a1a 0%, #4a4a4a 100%)", light: "gray.1" },
    "soft-dramatic": { primary: "grape.7", gradient: "linear-gradient(135deg, #862e9c 0%, #e64980 100%)", light: "grape.0" },
    "flamboyant-natural": { primary: "teal.7", gradient: "linear-gradient(135deg, #087f5b 0%, #20c997 100%)", light: "teal.0" },
    natural: { primary: "green.7", gradient: "linear-gradient(135deg, #2f9e44 0%, #8ce99a 100%)", light: "green.0" },
    "soft-natural": { primary: "orange.6", gradient: "linear-gradient(135deg, #e8590c 0%, #fcc419 100%)", light: "orange.0" },
    "dramatic-classic": { primary: "indigo.7", gradient: "linear-gradient(135deg, #364fc7 0%, #748ffc 100%)", light: "indigo.0" },
    classic: { primary: "blue.7", gradient: "linear-gradient(135deg, #1971c2 0%, #74c0fc 100%)", light: "blue.0" },
    "soft-classic": { primary: "cyan.7", gradient: "linear-gradient(135deg, #0c8599 0%, #66d9e8 100%)", light: "cyan.0" },
    "flamboyant-gamine": { primary: "violet.7", gradient: "linear-gradient(135deg, #7048e8 0%, #da77f2 100%)", light: "violet.0" },
    gamine: { primary: "lime.7", gradient: "linear-gradient(135deg, #66a80f 0%, #c0eb75 100%)", light: "lime.0" },
    "soft-gamine": { primary: "pink.6", gradient: "linear-gradient(135deg, #d6336c 0%, #fcc2d7 100%)", light: "pink.0" },
    "theatrical-romantic": { primary: "red.7", gradient: "linear-gradient(135deg, #c92a2a 0%, #ffa8a8 100%)", light: "red.0" },
    romantic: { primary: "pink.7", gradient: "linear-gradient(135deg, #a61e4d 0%, #f783ac 100%)", light: "pink.0" },
  };
  const accentColors = typeAccentColors[typeData.id] || typeAccentColors.classic;

  const heightStat =
    typeData.family === "gamine" ? "Petite (≤5'5\")" :
    typeData.family === "dramatic" || typeData.id === "flamboyant-natural" ? "Tall (5'7\"+)" :
    "Any Height";

  const styleVibe =
    typeData.family === "dramatic" ? "Bold & Sharp" :
    typeData.family === "natural" ? "Relaxed & Easy" :
    typeData.family === "classic" ? "Refined & Balanced" :
    typeData.family === "gamine" ? "Playful & Mixed" :
    "Soft & Feminine";

  const useLookbookImages = LOOKBOOK_TYPES.has(typeData.id);
  const useHairGallery = HAIR_GALLERY_TYPES.has(typeData.id);
  const useFaceImage = FACE_TYPES.has(typeData.id);
  const annotations = faceAnnotations[typeData.id];

  let sectionNumber = 0;
  const nextSection = () => {
    sectionNumber += 1;
    return sectionNumber;
  };

  const heightBar = extended?.heightBar;

  return (
    <Container size="xl" py={48}>
      <Stack gap={0}>
        {/* ---------- Hero ---------- */}
        <Box className={classes.hero} mb={56}>
          <Box>
            <p className={classes.heroKicker}>
              The Kibbe System &mdash; {typeData.family.charAt(0).toUpperCase() + typeData.family.slice(1)} Family
            </p>
            <h1 className={classes.heroTitle}>{typeData.name}</h1>
            <p className={classes.heroTagline}>{typeData.tagline}</p>

            <div className={classes.heroStats}>
              <div className={classes.heroStat}>
                <p className={classes.heroStatLabel}>Height Range</p>
                <p className={classes.heroStatValue}>{heightStat}</p>
              </div>
              <div className={classes.heroStat}>
                <p className={classes.heroStatLabel}>Bone Structure</p>
                <p className={classes.heroStatValue}>
                  {typeData.characteristics.bone[0].split(" ").slice(0, 2).join(" ")}
                </p>
              </div>
              <div className={classes.heroStat}>
                <p className={classes.heroStatLabel}>Yin / Yang</p>
                <p className={classes.heroStatValue}>{yinYangBalance.yin}% / {yinYangBalance.yang}%</p>
              </div>
              <div className={classes.heroStat}>
                <p className={classes.heroStatLabel}>Style Vibe</p>
                <p className={classes.heroStatValue}>{styleVibe}</p>
              </div>
            </div>
          </Box>

          <Box className={classes.heroImageWrap}>
            <Image
              src={`/kibbe/silhouettes/${typeData.id}.png`}
              alt={`${typeData.name} body type silhouette`}
              fit="contain"
              style={{ maxHeight: 420, width: "auto", maxWidth: "100%" }}
              fallbackSrc="https://placehold.co/600x800/f5f5f5/666?text=Silhouette"
            />
          </Box>
        </Box>

        <Box mb={56}><KibbeOffer source="type_guide" placement="hero" type={typeData.id} /></Box>

        {/* ---------- Overview ---------- */}
        <Box mb={56}>
          <SectionHeader n={nextSection()} label="Overview" title={`What Is a ${typeData.name}?`} />
          {(() => {
            const sentences = typeData.description
              .split(". ")
              .map((s, i, arr) => (i < arr.length - 1 ? `${s}.` : s));
            const lead = sentences[0];
            const rest = sentences.slice(1).join(" ");
            return (
              <>
                <Text className={classes.lede} fw={600} fz={20}>
                  {lead}
                </Text>
                {rest && (
                  <Text className={classes.ledeRest} mt={12}>
                    {rest}
                  </Text>
                )}
              </>
            );
          })()}
        </Box>

        {/* ---------- Physical Characteristics ---------- */}
        <Box mb={56}>
          <SectionHeader
            n={nextSection()}
            label="Physical Characteristics"
            title={`${typeData.name} Body & Face`}
          />
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing={40}>
            <div className={classes.charColumn}>
              <h3 className={classes.charSubhead}>Bone Structure</h3>
              {CHARACTERISTIC_TYPES.has(typeData.id) && (
                <img
                  src={`/kibbe/characteristics/${typeData.id}/bone.webp`}
                  alt={`${typeData.name} bone structure — annotated diagram`}
                  loading="lazy"
                  width={1400}
                  height={1867}
                  style={{ width: "100%", height: "auto", aspectRatio: "3/4", objectFit: "cover", borderRadius: 8, marginBottom: 16, border: "1px solid #eee9e2" }}
                />
              )}
              <p className={classes.keyTellLabel}>Key tell</p>
              <p className={classes.keyTellItem}>{typeData.characteristics.bone[0]}</p>
              <ul className={classes.editorialList}>
                {typeData.characteristics.bone.slice(1).map((item, i) => (
                  <li key={i} className={classes.editorialListItem}>{item}</li>
                ))}
              </ul>
            </div>
            <div className={classes.charColumn}>
              <h3 className={classes.charSubhead}>Flesh & Curve</h3>
              {CHARACTERISTIC_TYPES.has(typeData.id) && (
                <img
                  src={`/kibbe/characteristics/${typeData.id}/flesh.webp`}
                  alt={`${typeData.name} flesh and curve — annotated diagram`}
                  loading="lazy"
                  width={1400}
                  height={1867}
                  style={{ width: "100%", height: "auto", aspectRatio: "3/4", objectFit: "cover", borderRadius: 8, marginBottom: 16, border: "1px solid #eee9e2" }}
                />
              )}
              <p className={classes.keyTellLabel}>Key tell</p>
              <p className={classes.keyTellItem}>{typeData.characteristics.flesh[0]}</p>
              <ul className={classes.editorialList}>
                {typeData.characteristics.flesh.slice(1).map((item, i) => (
                  <li key={i} className={classes.editorialListItem}>{item}</li>
                ))}
              </ul>
            </div>
            <div className={classes.charColumn}>
              <h3 className={classes.charSubhead}>Facial Features</h3>
              {CHARACTERISTIC_TYPES.has(typeData.id) && (
                <img
                  src={`/kibbe/characteristics/${typeData.id}/facial.webp`}
                  alt={`${typeData.name} facial features — annotated diagram`}
                  loading="lazy"
                  width={1400}
                  height={1867}
                  style={{ width: "100%", height: "auto", aspectRatio: "3/4", objectFit: "cover", borderRadius: 8, marginBottom: 16, border: "1px solid #eee9e2" }}
                />
              )}
              <p className={classes.keyTellLabel}>Key tell</p>
              <p className={classes.keyTellItem}>{typeData.characteristics.facial[0]}</p>
              <ul className={classes.editorialList}>
                {typeData.characteristics.facial.slice(1).map((item, i) => (
                  <li key={i} className={classes.editorialListItem}>{item}</li>
                ))}
              </ul>
            </div>
          </SimpleGrid>
        </Box>

        {/* ---------- Celebrities ---------- */}
        <Box mb={56}>
          <SectionHeader n={nextSection()} label="Reference" title={`${typeData.name} Celebrities`} />
          <Text fz="sm" c="#6f6a64" mb={20}>
            Study these celebrities to understand how the {typeData.name} body type looks in practice.
          </Text>
          <SimpleGrid cols={{ base: 2, md: 3 }} spacing={24}>
            {(kibbeCelebrities[typeData.id] || []).map((celeb, i) => (
              <Box key={i}>
                <img
                  src={celeb.imageUrl}
                  loading="lazy"
                  alt={`${celeb.name} — ${typeData.name} celebrity example`}
                  referrerPolicy="no-referrer"
                  style={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: 8, marginBottom: 8 }}
                />
                <Anchor href={celeb.wikipediaUrl} target="_blank" rel="noopener noreferrer" size="sm" fw={500} c="dark" underline="hover">
                  {celeb.name}
                </Anchor>
                <Text size="xs" c="dimmed" mt={4} style={{ lineHeight: 1.5 }}>{celeb.caption}</Text>
              </Box>
            ))}
          </SimpleGrid>
          <Text size="xs" c="dimmed" mt={16}>Photos via Wikipedia.</Text>
        </Box>

        {/* ---------- The Lookbook ---------- */}
        <Box id="outfit-ideas" mb={40}>
          <SectionHeader n={nextSection()} label="The Lookbook" title={`${typeData.name} Outfit Ideas`} />
          <Text fz="sm" c="#6f6a64" mb={40}>
            {outfits.length} curated looks that flatter your body type &mdash;{" "}
            <Anchor component={Link} href={`/kibbe-outfits/${typeData.id}`} fz="sm">
              see all {typeData.name} outfits
            </Anchor>
          </Text>

          {outfits.map((outfit, i) => {
            const imageSrc = useLookbookImages
              ? `/kibbe/looks/${typeData.id}/${i + 1}.webp`
              : `/kibbe/types/${typeData.id}/${i + 1}.webp`;
            const reversed = i % 2 === 1;
            return (
              <div
                key={i}
                className={`${classes.lookRow} ${reversed ? classes.lookRowReversed : ""}`}
              >
                <div className={classes.lookImageCol}>
                  <div className={classes.lookImage}>
                    <Image
                      src={imageSrc}
                      alt={outfit.name}
                      fit="cover"
                      h="100%"
                      loading="lazy"
                      fallbackSrc={`https://placehold.co/600x800/f5f5f5/666?text=${encodeURIComponent(outfit.name)}`}
                    />
                  </div>
                </div>
                <div className={classes.lookTextCol}>
                  <p className={classes.lookKicker}>Look {String(i + 1).padStart(2, "0")}</p>
                  <h3 className={classes.lookName}>{outfit.name}</h3>
                  <p className={classes.lookDescription}>{outfit.description}</p>
                  <p className={classes.piecesLabel}>The pieces</p>
                  <ul className={classes.editorialList}>
                    {outfit.pieces.map((piece, pi) => (
                      <li key={pi} className={classes.editorialListItem}>{piece}</li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </Box>

        {/* ---------- Mid-page CTA (slim) ---------- */}
        <Box mb={56} className={`${classes.ctaBand} ${classes.ctaBandSlim}`}>
          <Text className={classes.ctaHeadline} fz={{ base: 20, md: 24 }}>
            See these looks in your colors
          </Text>
          <Text className={classes.ctaSubtitle} fz="sm">
            Your {typeData.name} outfits work best in your color season. One selfie finds it.
          </Text>
          <Group gap="sm" wrap="wrap">
            <Button
              component={Link}
              href="/ai-color-analysis"
              onClick={() => trackCtaClick("kibbe_type_colors_mid")}
              size="md"
              radius={999}
              fw={700}
              styles={{ root: { background: "white", color: "#141414" } }}
            >
              Get my color palette
            </Button>
            <Button
              component={Link}
              href={`/ai-outfit-generator?kibbe=${typeData.id}`}
              onClick={() => trackCtaClick("kibbe_type_outfits_mid")}
              variant="outline"
              size="md"
              radius={999}
              fw={700}
              styles={{ root: { borderColor: "white", color: "white" } }}
            >
              Generate {typeData.name} Outfits
            </Button>
          </Group>
        </Box>

        {/* ---------- The Rules ---------- */}
        <Box mb={56}>
          <SectionHeader n={nextSection()} label="The Rules" title={`${typeData.name} Style Rules`} />
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={40}>
            <div>
              <h3 className={classes.charSubhead}>Always</h3>
              <ul className={classes.numberedList}>
                {typeData.doStyles.map((item, i) => (
                  <li key={i} className={classes.numberedItem}>
                    <span className={classes.numberFigure}>{String(i + 1).padStart(2, "0")}</span>
                    <span className={classes.numberedItemText}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className={classes.charSubhead}>Never</h3>
              <ul className={classes.numberedList}>
                {typeData.dontStyles.map((item, i) => (
                  <li key={i} className={`${classes.numberedItem} ${classes.numberedItemMuted}`}>
                    <span className={classes.numberFigure}>{String(i + 1).padStart(2, "0")}</span>
                    <span className={classes.numberedItemText}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </SimpleGrid>
        </Box>

        {/* ---------- Fabrics, Patterns, Necklines ---------- */}
        <Box mb={56}>
          <SectionHeader n={nextSection()} label="Materials" title="Best Fabrics, Patterns & Necklines" />
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing={24}>
            <div className={classes.plainCard}>
              <p className={classes.plainCardTitle}>Fabrics</p>
              <ul className={classes.editorialList}>
                {typeData.fabrics.map((item, i) => (
                  <li key={i} className={classes.editorialListItem}>{item}</li>
                ))}
              </ul>
              <Image
                src={`/kibbe/guides/fabrics/${typeData.family}-fabrics.png`}
                alt={`Best fabrics for the ${typeData.name} Kibbe body type`}
                radius={4}
                mt={16}
                loading="lazy"
              />
            </div>

            <div className={classes.plainCard}>
              <p className={classes.plainCardTitle}>Patterns</p>
              <ul className={classes.editorialList}>
                {typeData.patterns.map((item, i) => (
                  <li key={i} className={classes.editorialListItem}>{item}</li>
                ))}
              </ul>
              <Image
                src={`/kibbe/guides/patterns/${typeData.family}-patterns.png`}
                alt={`Best patterns for the ${typeData.name} Kibbe body type`}
                radius={4}
                mt={16}
                loading="lazy"
              />
            </div>

            <div className={classes.plainCard}>
              <p className={classes.plainCardTitle}>Necklines</p>
              <ul className={classes.editorialList}>
                {typeData.necklines.map((item, i) => (
                  <li key={i} className={classes.editorialListItem}>{item}</li>
                ))}
              </ul>
              <Image
                src={`/kibbe/guides/necklines/${typeData.family}-necklines.png`}
                alt={`Best necklines for the ${typeData.name} Kibbe body type`}
                radius={4}
                mt={16}
                loading="lazy"
              />
            </div>
          </SimpleGrid>
        </Box>

        {extended && (
          <>
            {/* ---------- Hair ---------- */}
            <Box mb={56}>
              <SectionHeader n={nextSection()} label="Hair" title={`${typeData.name} Hair & Hairstyles`} />
              <Text fz="md" c="#44403b" mb={28} style={{ lineHeight: 1.8 }}>
                {extended.hair.intro}
              </Text>

              {useHairGallery ? (
                <ScrollArea type="auto" offsetScrollbars>
                  <div className={classes.hairScroll}>
                    {extended.hair.bestStyles.map((style, i) => (
                      <div key={i} className={classes.hairCard}>
                        <div className={classes.hairCardImage}>
                          <Image
                            src={`/kibbe/hair/${typeData.id}/${i + 1}.webp`}
                            alt={style.name}
                            fit="cover"
                            h="100%"
                            loading="lazy"
                            fallbackSrc={`https://placehold.co/400x520/f5f5f5/666?text=${encodeURIComponent(style.name)}`}
                          />
                        </div>
                        <p className={classes.hairCardName}>{style.name}</p>
                        <p className={classes.hairCardWhy}>{style.why}</p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <SimpleGrid cols={{ base: 1, md: 2 }} spacing={24}>
                  <ul className={classes.editorialList}>
                    {extended.hair.bestStyles.map((style, i) => (
                      <li key={i} className={classes.editorialListItem}>
                        <Text fw={600} fz="sm" span>{style.name}</Text>
                        <br />
                        <Text fz="sm" c="#6f6a64" span>{style.why}</Text>
                      </li>
                    ))}
                  </ul>
                  <Image
                    src={`/kibbe/hair/${typeData.id}.png`}
                    alt={`${typeData.name} Kibbe body type hairstyle ideas`}
                    radius={4}
                    loading="lazy"
                    fallbackSrc={`https://placehold.co/600x500/f5f5f5/666?text=${encodeURIComponent(typeData.name + " Hair")}`}
                  />
                </SimpleGrid>
              )}

              <p className={classes.avoidNote}>
                <span className={classes.avoidLabel}>Avoid</span>
                {extended.hair.avoid}
              </p>
            </Box>

            {/* ---------- Makeup ---------- */}
            <Box mb={56}>
              <SectionHeader n={nextSection()} label="Makeup" title={`${typeData.name} Makeup`} />
              <Text fz="md" c="#44403b" mb={28} style={{ lineHeight: 1.8 }}>
                {extended.makeup.intro}
              </Text>

              {extended.palette && (
                <div className={classes.paletteRow}>
                  {extended.palette.map((color, i) => (
                    <div key={i} className={classes.swatch}>
                      <div className={classes.swatchCircle} style={{ background: color.hex }} />
                      <span className={classes.swatchName}>{color.name}</span>
                    </div>
                  ))}
                </div>
              )}

              <ul className={classes.dashList}>
                {extended.makeup.tips.map((tip, i) => (
                  <li key={i} className={classes.dashItem}>
                    <span className={classes.dashMark}>&mdash;</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </Box>

            {/* ---------- The Face ---------- */}
            <Box mb={56}>
              <SectionHeader n={nextSection()} label="The Face" title={`The ${typeData.name} Face`} />
              {useFaceImage ? (
                <SimpleGrid cols={{ base: 1, md: 2 }} spacing={40}>
                  <div>
                    <div className={classes.faceImageWrap}>
                      <Image
                        src={`/kibbe/faces/${typeData.id}.webp`}
                        alt={`The ${typeData.name} face`}
                        fit="cover"
                        h="100%"
                        loading="lazy"
                        fallbackSrc="https://placehold.co/600x600/f5f5f5/666?text=Face"
                      />
                      {annotations?.map((a, i) => (
                        <div
                          key={i}
                          className={classes.faceDot}
                          style={{ left: `${a.dotX}%`, top: `${a.dotY}%` }}
                        >
                          {i + 1}
                        </div>
                      ))}
                    </div>
                    {annotations && (
                      <ul className={classes.faceCaptionList}>
                        {annotations.map((a, i) => (
                          <li key={i}>
                            <span className={classes.faceCaptionNumber}>{String(i + 1).padStart(2, "0")}</span>
                            {" — "}
                            {a.label}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div>
                    <Text fz="md" c="#44403b" style={{ lineHeight: 1.8 }}>
                      {extended.face.intro}
                    </Text>
                    <div className={classes.tellBlock}>
                      <Text fz="sm" fw={600} mb={6}>How to spot it</Text>
                      <Text fz="sm" c="#6f6a64" style={{ lineHeight: 1.7 }}>
                        {extended.face.tell}
                      </Text>
                    </div>
                  </div>
                </SimpleGrid>
              ) : (
                <SimpleGrid cols={{ base: 1, md: 2 }} spacing={40}>
                  <Text fz="md" c="#44403b" style={{ lineHeight: 1.8 }}>
                    {extended.face.intro}
                  </Text>
                  <div className={classes.tellBlock}>
                    <Text fz="sm" fw={600} mb={6}>How to spot it</Text>
                    <Text fz="sm" c="#6f6a64" style={{ lineHeight: 1.7 }}>
                      {extended.face.tell}
                    </Text>
                  </div>
                </SimpleGrid>
              )}
            </Box>

            {/* ---------- Height ---------- */}
            <Box mb={56}>
              <SectionHeader n={nextSection()} label="Height" title={`How Tall Are ${typeData.name}s?`} />
              {heightBar && (
                <Box maw={520} mb={24}>
                  <p className={classes.heightRangeLabel}>
                    {cmToFtIn(heightBar.minCm)} &ndash; {cmToFtIn(heightBar.maxCm)}
                  </p>
                  <div className={classes.heightTrack}>
                    <div
                      className={classes.heightBand}
                      style={{
                        left: `${heightPercent(heightBar.minCm)}%`,
                        width: `${heightPercent(heightBar.maxCm) - heightPercent(heightBar.minCm)}%`,
                        background: "#1a1a1a",
                      }}
                    />
                  </div>
                  <div className={classes.heightTicks}>
                    {HEIGHT_TICKS_CM.map((cm) => (
                      <div key={cm} className={classes.heightTick} style={{ left: `${heightPercent(cm)}%` }}>
                        <span className={classes.heightTickCm}>{cm}cm</span>
                        <span className={classes.heightTickFt}>{cmToFtIn(cm)}</span>
                      </div>
                    ))}
                  </div>
                </Box>
              )}
              <Text fz="md" c="#44403b" style={{ lineHeight: 1.8 }}>
                {extended.height.detail}
              </Text>
            </Box>

            {/* ---------- Plus Size ---------- */}
            <Box mb={56}>
              <SectionHeader n={nextSection()} label="Plus Size" title={`Plus Size ${typeData.name} Style`} />
              <Text fz="md" c="#44403b" mb={20} style={{ lineHeight: 1.8 }}>
                {extended.plusSize.intro}
              </Text>
              <ul className={classes.editorialList}>
                {extended.plusSize.styling.map((item, i) => (
                  <li key={i} className={classes.editorialListItem}>{item}</li>
                ))}
              </ul>
              <Anchor component={Link} href="/plus-size-kibbe-body-types" fz="sm" mt={16} display="inline-block">
                Read the full plus size Kibbe body types guide →
              </Anchor>
            </Box>
          </>
        )}

        {/* ---------- Common Mistypes ---------- */}
        <Box mb={56}>
          <SectionHeader n={nextSection()} label="Comparisons" title={`${typeData.name} vs Other Types`} />
          <Text fz="sm" c="#44403b" mb={24}>
            {typeData.name} is often confused with these types. Here&apos;s how to tell the difference:
          </Text>
          <Accordion variant="separated">
            {typeData.commonMistypes.map((mistype, i) => (
              <Accordion.Item key={i} value={mistype}>
                <Accordion.Control>
                  <Text fw={500} className={classes.serif} fz="lg">
                    {typeData.name} vs {mistype}
                  </Text>
                </Accordion.Control>
                <Accordion.Panel>
                  <Text fz="sm" c="#6f6a64" mb={12}>
                    {typeData.mistypeReasons[mistype]}
                  </Text>
                  {(() => {
                    const mistypeData = relatedTypes.find((t) => t?.name === mistype);
                    if (mistypeData && comparisonExists(typeData.id, mistypeData.id)) {
                      return (
                        <Button
                          component={Link}
                          href={`/kibbe/compare/${typeData.id}/${mistypeData.id}`}
                          variant="light"
                          size="sm"
                          rightSection={<IconChevronRight size={14} />}
                        >
                          Read full comparison guide
                        </Button>
                      );
                    }
                    return null;
                  })()}
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </Box>

        {/* ---------- FAQ ---------- */}
        <Box mb={56}>
          <SectionHeader n={nextSection()} label="FAQ" title="Frequently Asked Questions" />
          <Accordion variant="separated">
            <Accordion.Item value="how-to-know">
              <Accordion.Control>
                <Text fw={500} className={classes.serif} fz="lg">How do I know if I&apos;m a {typeData.name}?</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Text fz="sm" c="#6f6a64">
                  The best way to determine your Kibbe type is to look at your overall bone structure,
                  flesh distribution, and facial features. {typeData.name}s typically have{" "}
                  {typeData.characteristics.bone[0].toLowerCase()} and{" "}
                  {typeData.characteristics.flesh[0].toLowerCase()}. If you&apos;re unsure, our AI-powered
                  Kibbe analysis can suggest a starting point from your photo and explain alternatives. It is an independent AI estimate, not an official or verified typing.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value="height">
              <Accordion.Control>
                <Text fw={500} className={classes.serif} fz="lg">What height is typical for {typeData.name}?</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Text fz="sm" c="#6f6a64">
                  {typeData.family === "gamine"
                    ? `${typeData.name}s are typically 5'5" (165cm) or under. The Gamine types have a petite frame that's essential to their type.`
                    : typeData.family === "dramatic" || typeData.id === "flamboyant-natural"
                      ? `${typeData.name}s are typically 5'7" (170cm) or taller. Their long vertical line is a key characteristic.`
                      : `${typeData.name}s can be any height, though most fall in the moderate range. Height alone doesn't determine Kibbe type - it's about proportions and bone structure.`}
                </Text>
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value="weight">
              <Accordion.Control>
                <Text fw={500} className={classes.serif} fz="lg">Does weight affect my Kibbe type?</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Text fz="sm" c="#6f6a64">
                  No - your Kibbe type is determined by your bone structure, which doesn&apos;t change
                  with weight. A {typeData.name} at any weight will still have the same bone structure
                  and proportions. Weight only affects how flesh sits on your frame, not the frame itself.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value="celebrities">
              <Accordion.Control>
                <Text fw={500} className={classes.serif} fz="lg">Are celebrity Kibbe types accurate?</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Text fz="sm" c="#6f6a64">
                  Celebrity typing is done by the Kibbe community and should be taken as a guide, not
                  gospel. Only David Kibbe can officially type someone. However, studying celebrities
                  who are commonly typed as {typeData.name} can help you understand how the type looks
                  in practice.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </Box>

        {/* ---------- Color season bridge ---------- */}
        <Box mb={56}>
          <p className={classes.kicker}>Beyond Body Lines</p>
          <h2 className={classes.sectionTitle} style={{ fontSize: "clamp(1.5rem, 2.8vw, 2.1rem)" }}>
            Your lines are only half the equation &mdash; color is the other
          </h2>
          <Text fz="md" c="#44403b" maw={640} style={{ lineHeight: 1.8 }} mb={20}>
            Kibbe describes your body&apos;s lines &mdash; the bone structure and flesh that decide which
            silhouettes and fabrics work for you. Seasonal color analysis describes something different:
            which colors harmonize with your skin, hair, and eyes. A {typeData.name} in their right season
            reads sharper and more intentional than a {typeData.name} in the wrong one. Put together, line
            and color make a complete style identity.
          </Text>
          <Anchor
            component={Link}
            href="/color-palette"
            className={classes.bridgeLink}
            onClick={() =>
              trackGizmo("kibbe_palette_bridge_click", { type: typeData.id, target: "index" })
            }
          >
            Explore the 12 seasonal color palettes →
          </Anchor>
          <div className={classes.bridgeSeasonRow}>
            {(["spring", "summer", "autumn", "winter"] as const).map((season) => (
              <Anchor
                key={season}
                component={Link}
                href={`/color-palette/${season}`}
                className={classes.bridgeSeasonLink}
                onClick={() =>
                  trackGizmo("kibbe_palette_bridge_click", { type: typeData.id, target: season })
                }
              >
                {season.charAt(0).toUpperCase() + season.slice(1)}
              </Anchor>
            ))}
          </div>
        </Box>

        {/* ---------- Bottom CTA ---------- */}
        <Box mb={40}><KibbeOffer source="type_guide" placement="footer" type={typeData.id} showSample={false} /></Box>
        <Group mb={40} gap="lg">
          <Anchor component={Link} href={`/kibbe-outfits/${typeData.id}`}>Browse free {typeData.name} outfit examples →</Anchor>
          <Anchor component={Link} href={`/ai-outfit-generator?kibbe=${typeData.id}`} onClick={() => trackCtaClick("kibbe_type_outfits_bottom")}>Explore the separate AI outfit generator →</Anchor>
        </Group>

        {/* Gamine family hub link */}
        {typeData.family === "gamine" && (
          <Paper p="lg" radius={4} withBorder mb={40} style={{ borderColor: "#e7e2db" }}>
            <Group justify="space-between" wrap="wrap" gap="md">
              <Box>
                <Text fw={600} mb={4}>New to the gamine archetype?</Text>
                <Text fz="sm" c="#6f6a64">
                  What &quot;gamine&quot; actually means, where the word comes from, and how all three gamine types differ.
                </Text>
              </Box>
              <Button
                component={Link}
                href="/what-is-gamine"
                variant="light"
                color={accentColors.primary.split(".")[0]}
                rightSection={<IconChevronRight size={16} />}
              >
                What Is a Gamine?
              </Button>
            </Group>
          </Paper>
        )}

        {/* ---------- Email capture ---------- */}
        <Box mb={40}>
          <EmailResultsCapture
            source="kibbe-type-page"
            resultLabel={typeData.name}
            title={`Get the ${typeData.name} style cheat sheet`}
            subtitle="Your best silhouettes, the colors to try first, and outfit formulas — straight to your inbox."
          />
        </Box>

        {/* ---------- Related Types ---------- */}
        <Box>
          <p className={classes.kicker}>Explore</p>
          <h3 className={classes.sectionTitle} style={{ fontSize: 22 }}>Other Kibbe Types</h3>
          <hr className={classes.hairline} style={{ marginBottom: 20 }} />
          <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing={12}>
            {Object.entries(kibbeTypes)
              .filter(([id]) => id !== typeData.id)
              .map(([id, type]) => (
                <Button
                  key={id}
                  component={Link}
                  href={`/kibbe/${id}`}
                  variant="default"
                  size="sm"
                >
                  {type.name}
                </Button>
              ))}
          </SimpleGrid>
        </Box>
      </Stack>
    <KibbeConceptLinks /></Container>
  );
}
